"use client"

import { useState, useEffect, useRef } from "react"
import HomeDetailsWrapper from "./home-details-wrapper"
import { Accordion } from "../../../shared/components/ui/accordion"
import HomeTypeForm from "./forms/home-type-form"
import HomeRoofingForm from "./forms/home-roofing-form"
import HomeMuckGutForm from "./forms/home-muck-gut-form"
import HomeMoldRemediationForm from "./forms/home-mold-remediation-form"
import HomeRebuildPrepForm from "./forms/home-rebuild-prep-form"
import HomeRoughCarpentaryForm from "./forms/home-rough-carpentary-form"
import HomeElectricalForm from "./forms/home-electrical-form"
import HomeHVACForm from "./forms/home-hvac-form"
import HomePlumbingForm from "./forms/home-plumbing-form"
import HomeBathroomRebuildForm from "./forms/home-bathroom-rebuild-form"
import HomeCrawlSpaceForm from "./forms/home-crawl-space-form"
import HomeInsulationInstallForm from "./forms/home-insulation-install-form"
import HomeDrywallHangingForm from "./forms/home-drywall-hanging-form"
import HomeMudTapeForm from "./forms/home-mud-tape-form"
import HomeBlockWallForm from "./forms/home-block-wall-form"
import HomeStuccoForm from "./forms/home-stucco-form"
import HomeCabinetInstallForm from "./forms/home-cabinet-install-form"
import HomeInteriorPaintForm from "./forms/home-interior-paint-form"
import HomeInstallInteriorDoorsForm from "./forms/home-install-interior-doors-form"
import HomeTrimInstallForm from "./forms/home-trim-install-form"
import HomeFlooringInstallForm from "./forms/home-flooring-install-form"
import HomeExteriorDoorInstallForm from "./forms/home-exterior-door-install-form"
import HomeDrivewayRepairForm from "./forms/home-driveway-repair-form"
import HomeStormShuttersForm from "./forms/home-storm-shutters-form"
import HomeMiscellaneousForm from "./forms/home-miscellaneous-form"
import HomeHomeStatusForm from "./forms/home-home-status-form"
import HomePermitInspectionStatusForm from "./forms/home-permit-inspection-status-form"
import TreeDebrisInfoForm from "./forms/tree-derbis-info-form"
import HomeOverallStatusForm from "./forms/home-overall-status-form"
import { useFormContext, useWatch } from "react-hook-form"
import { HomeWithHomeOwnerFormType } from "../schema/home-with-home-owner.schema"
import { useActiveWorkScopes } from "../hooks/use-active-work-scopes"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"

export const ALL_ACCORDION_ITEMS = [
    "Overall Status",
    "Home Type & Repairability Info",
    "Home Status",
    "Tree, Exterior Debris & Heavy Equipment",
    "Roofing",
    "Crawl Space",
    "Muck & Gut",
    "Mold Remediation",
    "Rebuild Prep",
    "Rough Carpentry",
    "Electrical",
    "HVAC",
    "Plumbing",
    "Insulation Install",
    "Drywall Hanging",
    "Mud and Tape",
    "Block Wall",
    "Rendering/Stucco",
    "Bathroom Rebuild",
    "Cabinet Install",
    "Interior Paint",
    "Flooring Install",
    "Install Interior Doors",
    "Trim Install",
    "Exterior Door Install",
    "Driveway Repair",
    "Storm Shutters",
    "Miscellaneous Goodwill",
    "Permit Inspection Status"
]

// Map accordion headings to their status field paths
// For accordions with multiple status fields, use an array of paths.
// Multi-field sections collapse only when ALL sub-workscope statuses are in COLLAPSED_STATUSES.
const ACCORDION_STATUS_MAP: Record<string, string | string[]> = {
    "Home Type & Repairability Info": "home.repairabilityInfo.assessmentCompleted",
    "Tree, Exterior Debris & Heavy Equipment": [
        "home.treeDebrisInfo.treeWorkNeeded",
        "home.treeDebrisInfo.exteriorDebrisRemovalNeeded",
        "home.treeDebrisInfo.heavyEquipmentNeeded",
    ],
    "Roofing": [
        "home.roofingInfo.roofing",
        "home.roofingInfo.roofTarpNeeded",
    ],
    "Crawl Space": ["home.crawlSpaceInfo.crawlSpaceMuck", "home.crawlSpaceInfo.crawlSpaceInsulation"],
    "Muck & Gut": "home.muckGutInfo.muckGutNeeded",
    "Mold Remediation": "home.moldRemediationInfo.moldRemediation",
    "Rebuild Prep": "home.rebuildPrepInfo.rebuildPrep",
    "Rough Carpentry": "home.roughCarpentryInfo.roughCarpentry",
    "Electrical": [
        "home.electricalInfo.electrical",
        "home.electricalInfo.electricalRoughIn",
    ],
    "HVAC": [
        "home.hvacInfo.hvac",
        "home.hvacInfo.mechanicalRoughIn",
    ],
    "Plumbing": [
        "home.plumbingInfo.plumbing",
        "home.plumbingInfo.plumbingRoughIn",
    ],
    "Insulation Install": "home.insulationInstallInfo.insulationInstall",
    "Drywall Hanging": "home.drywallHangingInfo.drywallHanging",
    "Mud and Tape": "home.mudAndTapeInfo.mudAndTape",
    "Block Wall": "home.blockWallInfo.blockWallRepair",
    "Rendering/Stucco": "home.stuccoInfo.stucco",
    "Bathroom Rebuild": "home.bathroomRebuildInfo.bathroomRebuild",
    "Cabinet Install": [
        "home.cabinetInstallInfo.cabinetInstall",
        "home.cabinetInstallInfo.counterInstall",
        "home.cabinetInstallInfo.applianceInstall",
    ],
    "Interior Paint": "home.interiorPaintInfo.interiorPaint",
    "Flooring Install": "home.flooringInstallInfo.flooringInstall",
    "Install Interior Doors": "home.installInteriorDoorsInfo.installInteriorDoors",
    "Trim Install": "home.trimInstallInfo.trimInstall",
    "Exterior Door Install": "home.exteriorDoorInstallInfo.exteriorDoorInstall",
    "Driveway Repair": "home.drivewayRepair.drivewayRepair",
    "Storm Shutters": "home.stormShutters.shutterInstall",
    "Miscellaneous Goodwill": "home.miscellaneousInfo.miscellaneous",
}

// Map accordion headings to their work scope names (for filtering by active status)
const ACCORDION_WORK_SCOPE_MAP: Record<string, string> = {
    "Home Type & Repairability Info": "assess-needs",
    "Tree, Exterior Debris & Heavy Equipment": "tree-work", // Primary work scope for this section
    "Roofing": "install-roofing",
    "Crawl Space": "m-and-g-crawl-space", // Primary work scope for this section
    "Muck & Gut": "muck-and-gut-interior",
    "Mold Remediation": "mold-remediation",
    "Rebuild Prep": "rebuild-prep",
    "Rough Carpentry": "rough-carpentry",
    "Electrical": "electrical-fixtures",
    "HVAC": "hvac-final",
    "Plumbing": "plumbing-fixtures",
    "Insulation Install": "insulation",
    "Drywall Hanging": "drywall-hanging",
    "Mud and Tape": "mud-and-tape",
    "Block Wall": "block-wall",
    "Rendering/Stucco": "stucco",
    "Bathroom Rebuild": "bathroom-rebuild",
    "Cabinet Install": "install-cabinets",
    "Interior Paint": "interior-paint",
    "Flooring Install": "flooring-install",
    "Install Interior Doors": "install-interior-doors",
    "Trim Install": "trim-install",
    "Exterior Door Install": "install-exterior-doors",
    "Driveway Repair": "repair-driveway",
    "Storm Shutters": "shutter-install",
    "Miscellaneous Goodwill": "misc",
}

// Statuses that should be collapsed by default
const COLLAPSED_STATUSES = ["No Assistance Required", "Complete", "Materials Only Complete"]

// Goodwill statuses that should expand the accordion (same idea as workscope status)
const GOODWILL_EXPAND_STATUSES = ["Needed Not Started", "25%", "50%", "75%", "Materials Only", "Unknown"]

// For each accordion that has Goodwill: list of [goodwillOnPath, goodwillStatusPath] pairs.
// Accordion expands if any pair has on=true and status in GOODWILL_EXPAND_STATUSES.
const ACCORDION_GOODWILL_MAP: Record<string, [string, string][]> = {
    "Tree, Exterior Debris & Heavy Equipment": [
        ["home.treeDebrisInfo.goodwillTreeWorkOn", "home.treeDebrisInfo.goodwillTreeWorkNeeded"],
        ["home.treeDebrisInfo.goodwillExteriorDebrisOn", "home.treeDebrisInfo.goodwillExteriorDebrisRemovalNeeded"],
        ["home.treeDebrisInfo.goodwillHeavyEquipmentOn", "home.treeDebrisInfo.goodwillHeavyEquipmentNeeded"],
    ],
    "Roofing": [
        ["home.roofingInfo.goodwillRoofingOn", "home.roofingInfo.goodwillRoofing"],
        ["home.roofingInfo.goodwillRoofTarpOn", "home.roofingInfo.goodwillRoofTarpNeeded"],
    ],
    "Crawl Space": [
        ["home.crawlSpaceInfo.goodwillCrawlSpaceInsulationOn", "home.crawlSpaceInfo.goodwillCrawlSpaceInsulation"],
        ["home.crawlSpaceInfo.goodwillCrawlSpaceMuckOn", "home.crawlSpaceInfo.goodwillCrawlSpaceMuck"],
    ],
    "Muck & Gut": [["home.muckGutInfo.goodwillMuckGutNeededOn", "home.muckGutInfo.goodwillMuckGutNeeded"]],
    "Mold Remediation": [["home.moldRemediationInfo.goodwillMoldRemediationOn", "home.moldRemediationInfo.goodwillMoldRemediation"]],
    "Rebuild Prep": [["home.rebuildPrepInfo.goodwillRebuildPrepOn", "home.rebuildPrepInfo.goodwillRebuildPrep"]],
    "Rough Carpentry": [["home.roughCarpentryInfo.goodwillRoughCarpentryOn", "home.roughCarpentryInfo.goodwillRoughCarpentry"]],
    "Electrical": [
        ["home.electricalInfo.goodwillElectricalOn", "home.electricalInfo.goodwillElectrical"],
        ["home.electricalInfo.goodwillElectricalRoughInOn", "home.electricalInfo.goodwillElectricalRoughIn"],
    ],
    "HVAC": [
        ["home.hvacInfo.goodwillHvacOn", "home.hvacInfo.goodwillHvac"],
        ["home.hvacInfo.goodwillMechanicalRoughInOn", "home.hvacInfo.goodwillMechanicalRoughIn"],
    ],
    "Plumbing": [
        ["home.plumbingInfo.goodwillPlumbingOn", "home.plumbingInfo.goodwillPlumbing"],
        ["home.plumbingInfo.goodwillPlumbingRoughInOn", "home.plumbingInfo.goodwillPlumbingRoughIn"],
    ],
    "Insulation Install": [["home.insulationInstallInfo.goodwillInsulationInstallOn", "home.insulationInstallInfo.goodwillInsulationInstall"]],
    "Drywall Hanging": [["home.drywallHangingInfo.goodwillDrywallHangingOn", "home.drywallHangingInfo.goodwillDrywallHanging"]],
    "Mud and Tape": [["home.mudAndTapeInfo.goodwillMudAndTapeOn", "home.mudAndTapeInfo.goodwillMudAndTape"]],
    "Block Wall": [["home.blockWallInfo.goodwillBlockWallRepairOn", "home.blockWallInfo.goodwillBlockWallRepair"]],
    "Rendering/Stucco": [["home.stuccoInfo.goodwillStuccoOn", "home.stuccoInfo.goodwillStucco"]],
    "Bathroom Rebuild": [["home.bathroomRebuildInfo.goodwillBathroomRebuildOn", "home.bathroomRebuildInfo.goodwillBathroomRebuild"]],
    "Cabinet Install": [
        ["home.cabinetInstallInfo.goodwillCabinetInstallOn", "home.cabinetInstallInfo.goodwillCabinetInstall"],
        ["home.cabinetInstallInfo.goodwillCounterInstallOn", "home.cabinetInstallInfo.goodwillCounterInstall"],
        ["home.cabinetInstallInfo.goodwillApplianceInstallOn", "home.cabinetInstallInfo.goodwillApplianceInstall"],
    ],
    "Interior Paint": [["home.interiorPaintInfo.goodwillInteriorPaintOn", "home.interiorPaintInfo.goodwillInteriorPaint"]],
    "Flooring Install": [["home.flooringInstallInfo.goodwillFlooringInstallOn", "home.flooringInstallInfo.goodwillFlooringInstall"]],
    "Install Interior Doors": [["home.installInteriorDoorsInfo.goodwillInstallInteriorDoorsOn", "home.installInteriorDoorsInfo.goodwillInstallInteriorDoors"]],
    "Trim Install": [["home.trimInstallInfo.goodwillTrimInstallOn", "home.trimInstallInfo.goodwillTrimInstall"]],
    "Exterior Door Install": [["home.exteriorDoorInstallInfo.goodwillExteriorDoorInstallOn", "home.exteriorDoorInstallInfo.goodwillExteriorDoorInstall"]],
    "Driveway Repair": [["home.drivewayRepair.goodwillDrivewayRepairOn", "home.drivewayRepair.goodwillDrivewayRepair"]],
    "Storm Shutters": [["home.stormShutters.goodwillShutterInstallOn", "home.stormShutters.goodwillShutterInstall"]],
}

function hasGoodwillExpandStatus(formValues: any, accordionHeading: string): boolean {
    const pairs = ACCORDION_GOODWILL_MAP[accordionHeading]
    if (!pairs) return false
    for (const [onPath, statusPath] of pairs) {
        const on = getNestedValue(formValues, onPath)
        const status = getNestedValue(formValues, statusPath)
        if (on && status && GOODWILL_EXPAND_STATUSES.includes(status)) {
            return true
        }
    }
    return false
}

function calculateDefaultOpenItems(formValues: any): string[] {
    const openItems: string[] = []
    
    // Always include "Overall Status"
    openItems.push("Overall Status")
    
    // Check Assessment status
    const assessmentStatus = getNestedValue(formValues, "home.repairabilityInfo.assessmentCompleted")
    // If Assessment is "Needed Not Started", "Reassessment Needed", or blank/null (not yet assessed), expand all for assessor
    const assessmentNotDone = !assessmentStatus || assessmentStatus === "Needed Not Started" || assessmentStatus === "Reassessment Needed"
    
    // Check each accordion's status
    for (const [accordionHeading, statusFieldPath] of Object.entries(ACCORDION_STATUS_MAP)) {
        // If Assessment not done, expand everything regardless of status
        if (assessmentNotDone) {
            openItems.push(accordionHeading)
        } else {
            // Normal logic: check status and collapse if needed
            // Handle multiple status fields (e.g. Tree/Debris/Heavy Equipment, Crawl Space).
            // Collapse only when ALL sub-workscope statuses are in COLLAPSED_STATUSES.
            // Expand if any sub-workscope still has need (or is blank).
            let shouldOpen = false
            if (Array.isArray(statusFieldPath)) {
                const allCollapsed = statusFieldPath.every((path) => {
                    const status = getNestedValue(formValues, path)
                    return status && COLLAPSED_STATUSES.includes(status)
                })
                shouldOpen = !allCollapsed
            } else {
                // Single status field
                const status = getNestedValue(formValues, statusFieldPath)
                // Expand if status is blank/null/undefined or not in collapsed statuses
                shouldOpen = !status || !COLLAPSED_STATUSES.includes(status)
            }
            // Also expand if any Goodwill status in this section is "in progress" (Needed Not Started, 25%, 50%, 75%, Materials Only, Unknown)
            if (!shouldOpen && hasGoodwillExpandStatus(formValues, accordionHeading)) {
                shouldOpen = true
            }
            if (shouldOpen) {
                openItems.push(accordionHeading)
            }
        }
    }
    
    // If Assessment not done, also expand "Home Status" and "Permit Inspection Status"
    if (assessmentNotDone) {
        if (!openItems.includes("Home Status")) {
            openItems.push("Home Status")
        }
        if (!openItems.includes("Permit Inspection Status")) {
            openItems.push("Permit Inspection Status")
        }
    }
    // Otherwise, "Home Status" and "Permit Inspection Status" are collapsed by default (not included)
    
    return openItems
}

// Helper function to get nested object value by path string
function getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj)
}

export const DEFAULT_OPEN_ITEMS = ALL_ACCORDION_ITEMS

type HomeDetailsFormAccordionProps = {
    openItems: string[]
    setOpenItems: (items: string[]) => void
}

function HomeDetailsFormAccordion({ openItems, setOpenItems }: HomeDetailsFormAccordionProps) {
    const { control } = useFormContext<HomeWithHomeOwnerFormType>()
    const formValues = useWatch({ control })
    const hasSetDefaults = useRef(false)
    const { activeWorkScopes, loading: loadingWorkScopes } = useActiveWorkScopes()
    const { session } = useSession()

    // Home Type & Repairability Info, Home Status, and Permit & Inspection Status are hidden for Volunteers and Supporters
    const isVolunteerOrSupporter = session?.role === UserRole.VOLUNTEER || session?.role === UserRole.Supporter

    // Helper function to check if an accordion section should be visible
    const isAccordionVisible = (heading: string): boolean => {
        // Hide for Volunteers and Supporters: Home Type & Repairability Info, Home Status, Permit & Inspection Status
        if (heading === "Home Type & Repairability Info" || heading === "Home Status" || heading === "Permit Inspection Status") {
            return !isVolunteerOrSupporter
        }
        // Always show Overall Status
        if (heading === "Overall Status") {
            return true
        }
        
        // If still loading, show all sections
        if (loadingWorkScopes) {
            return true
        }
        
        // Get the work scope for this accordion section
        const workScope = ACCORDION_WORK_SCOPE_MAP[heading]
        if (!workScope) {
            // If no mapping found, show by default (safety fallback)
            return true
        }
        
        // Check if the work scope is active
        // If the work scope is not in the map, default to true (show it)
        return activeWorkScopes[workScope] !== false
    }
    
    // Calculate default open items based on form values when component mounts
    useEffect(() => {
        if (formValues && !hasSetDefaults.current && openItems.length === 0) {
            const defaultOpen = calculateDefaultOpenItems(formValues)
            setOpenItems(defaultOpen)
            hasSetDefaults.current = true
        }
    }, [formValues, openItems.length, setOpenItems]) // Run when form values are available

    return (
        <>
            <Accordion
                type="multiple"
                className='space-y-5'
                value={openItems}
                onValueChange={setOpenItems}
            >
                <HomeOverallStatusForm />
            </Accordion>
            <Accordion
                type="multiple"
                className='space-y-5'
                value={openItems}
                onValueChange={setOpenItems}
            >
                {isAccordionVisible("Home Type & Repairability Info") && <HomeTypeForm />}
                {isAccordionVisible("Home Status") && <HomeHomeStatusForm />}
                {isAccordionVisible("Tree, Exterior Debris & Heavy Equipment") && <TreeDebrisInfoForm />}
                {isAccordionVisible("Roofing") && <HomeRoofingForm />}
                {isAccordionVisible("Crawl Space") && <HomeCrawlSpaceForm />}
                {isAccordionVisible("Muck & Gut") && <HomeMuckGutForm />}
                {isAccordionVisible("Mold Remediation") && <HomeMoldRemediationForm />}
                {isAccordionVisible("Rebuild Prep") && <HomeRebuildPrepForm />}
                {isAccordionVisible("Rough Carpentry") && <HomeRoughCarpentaryForm />}
                {isAccordionVisible("Electrical") && <HomeElectricalForm />}
                {isAccordionVisible("HVAC") && <HomeHVACForm />}
                {isAccordionVisible("Plumbing") && <HomePlumbingForm />}
                {isAccordionVisible("Insulation Install") && <HomeInsulationInstallForm />}
                {isAccordionVisible("Drywall Hanging") && <HomeDrywallHangingForm />}
                {isAccordionVisible("Mud and Tape") && <HomeMudTapeForm />}
                {isAccordionVisible("Block Wall") && <HomeBlockWallForm />}
                {isAccordionVisible("Rendering/Stucco") && <HomeStuccoForm />}
                {isAccordionVisible("Bathroom Rebuild") && <HomeBathroomRebuildForm />}
                {isAccordionVisible("Cabinet Install") && <HomeCabinetInstallForm />}
                {isAccordionVisible("Interior Paint") && <HomeInteriorPaintForm />}
                {isAccordionVisible("Flooring Install") && <HomeFlooringInstallForm />}
                {isAccordionVisible("Install Interior Doors") && <HomeInstallInteriorDoorsForm />}
                {isAccordionVisible("Trim Install") && <HomeTrimInstallForm />}
                {isAccordionVisible("Exterior Door Install") && <HomeExteriorDoorInstallForm />}
                {isAccordionVisible("Driveway Repair") && <HomeDrivewayRepairForm />}
                {isAccordionVisible("Storm Shutters") && <HomeStormShuttersForm />}
                {isAccordionVisible("Miscellaneous Goodwill") && <HomeMiscellaneousForm />}
                {isAccordionVisible("Permit Inspection Status") && <HomePermitInspectionStatusForm />}
            </Accordion>
        </>
    )
}

export default HomeDetailsFormAccordion
